import { action, makeAutoObservable } from 'mobx';
import { makePersistable, clearPersistedStore } from 'mobx-persist-store';

import { persistConfig } from 'store/config';
import { ServiceType, type JsonDataItem, type JsonData, type TimeSlot } from 'types';

class BookingStore {
  // booking data
  isNewCustomer: boolean = false;
  serviceType: ServiceType = ServiceType.OnSite;
  step: number = 0;
  notes: string[] = [];
  callback: JsonDataItem | null = null;
  servicePrice: JsonDataItem | null = null;
  isNotified: boolean | null = null;
  isAvailabilityOverrideEnabled: boolean = false;

  // service info
  serviceList: JsonDataItem | null = null;
  productList: JsonData | null = null;

  // api responses
  customer: JsonDataItem | null = null;
  serviceSelection: JsonDataItem | null = null;
  appointment: JsonDataItem | null = null;
  job: JsonDataItem | null = null;
  previousJob: JsonDataItem | null = null;
  techniciansList: JsonData | null = null;
  paymentInfo: JsonDataItem | null = null;
  lostJob: JsonDataItem | null = null;
  lostJobReasons: JsonDataItem | null = null;

  // form selection
  location: JsonDataItem = {};
  productSelection: JsonDataItem | null = null;
  slot: TimeSlot | null = null;
  technician: JsonDataItem | null = null;
  paymentMethod: JsonData | null = null;

  private static instance: BookingStore;
  private constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    void makePersistable(this, {
      name: 'booking-store',
      properties: [
        // booking data
        'isNewCustomer',
        'serviceType',
        'step',
        'notes',
        'callback',
        'servicePrice',
        'isNotified',
        'isAvailabilityOverrideEnabled',

        // service info
        'serviceList',
        'productList',

        // api responses
        'customer',
        'serviceSelection',
        'appointment',
        'job',
        'previousJob',
        'techniciansList',
        'paymentInfo',
        'lostJob',
        'lostJobReasons',

        // form selection
        'location',
        'productSelection',
        'slot',
        'technician',
        'paymentMethod',
      ],
      ...persistConfig,
    });
  }

  @action
  setIsNewCustomer = (isNewCustomer: boolean) => {
    this.isNewCustomer = isNewCustomer;
  };

  @action
  setServiceType = (type: ServiceType) => {
    this.serviceType = type;
  };

  @action
  setNotes = (notes: string[]) => {
    this.notes = notes;
  };

  @action
  setStep = (step: number) => {
    this.step = step;
  };

  @action
  setCallback = (callback: JsonDataItem | null) => {
    this.callback = callback;
  };

  @action
  setServicePrice = (price: JsonDataItem | null) => {
    this.servicePrice = price;
  };

  @action
  setIsNotified = (status: boolean) => {
    this.isNotified = status;
  };

  @action
  setIsAvailabilityOverrideEnabled = (isEnabled: boolean) => {
    this.isAvailabilityOverrideEnabled = isEnabled;
  };

  // service info
  @action
  setServiceList = (services: JsonData) => {
    this.serviceList = services;
  };

  @action
  setProductList = (products: JsonData) => {
    this.productList = products;
  };

  // api responses
  @action
  setCustomer = (record: JsonDataItem | null) => {
    this.customer = record;
  };

  @action
  setServiceSelection = (selection: JsonDataItem | null) => {
    this.serviceSelection = selection;
  };

  @action
  setAppointment = (appointment: JsonDataItem | null) => {
    this.appointment = appointment;
  };

  @action
  setJob = (job: JsonDataItem) => {
    this.job = job;
  };

  @action
  setPreviousJob = (job: JsonDataItem) => {
    this.previousJob = job;
  };

  @action
  setTechniciansList = (list: JsonData) => {
    this.techniciansList = list;
  };

  @action
  setPaymentInfo = (info: JsonDataItem) => {
    this.paymentInfo = info;
  };

  @action
  setLostJob = (lostJob: JsonDataItem | null) => {
    this.lostJob = lostJob;
  };

  @action
  setLostJobReasons = (reasons: JsonDataItem | null) => {
    this.lostJobReasons = reasons;
  };

  // form selection
  @action
  setLocation = (location: JsonDataItem) => {
    this.location = location;
  };

  @action
  setProductSelection = (selection: JsonDataItem | null) => {
    this.productSelection = selection;
  };

  @action
  setSlot = (record: TimeSlot | null) => {
    this.slot = record;
  };

  @action
  setTechnician = (record: JsonDataItem | null) => {
    this.technician = record;
  };

  @action
  setPaymentMethod = (method: JsonData) => {
    this.paymentMethod = method;
  };

  // reset
  @action
  resetBookingProgress = () => {
    // booking data
    this.isNewCustomer = false;
    this.serviceType = ServiceType.OnSite;
    this.step = 0;
    this.notes = [];
    this.callback = null;
    this.servicePrice = null;
    this.isNotified = null;
    this.isAvailabilityOverrideEnabled = false;

    // service info
    this.serviceList = null;
    this.productList = null;

    // api responses
    this.customer = null;
    this.serviceSelection = null;
    this.appointment = null;
    this.job = null;
    this.previousJob = null;
    this.techniciansList = null;
    this.paymentInfo = null;
    this.lostJob = null;
    this.lostJobReasons = null;

    // form selection
    this.location = {};
    this.productSelection = null;
    this.slot = null;
    this.technician = null;
    this.paymentMethod = null;

    void clearPersistedStore(this);
  };

  // Singleton pattern
  public static getInstance(): BookingStore {
    if (!BookingStore.instance) {
      BookingStore.instance = new BookingStore();
    }
    return BookingStore.instance;
  }
}

export default BookingStore.getInstance();
